import React, { useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function loadGoogleMapsScript(apiKey) {
  return new Promise((resolve, reject) => {
    if (window.google?.maps?.places) return resolve();

    const existing = document.getElementById("google-maps-script");
    if (existing) {
      existing.addEventListener("load", resolve);
      return;
    }

    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;

    document.body.appendChild(script);
  });
}

export function GoogleAddressSearch({
  label = "Google Address",
  value,
  onChangeValue,
  onSelectPlace,
  restrictCountries = ["IN"]
}) {
  const inputRef = useRef(null);
  const acRef = useRef(null);

  const parsePincode = (components = []) => {
    const pin = components.find((c) => c.types?.includes("postal_code"));
    return pin?.long_name || "";
  };

  useEffect(() => {
    const init = async () => {
      try {
        if (!GOOGLE_MAPS_API_KEY) return;

        await loadGoogleMapsScript(GOOGLE_MAPS_API_KEY);

        if (!inputRef.current) return;

        acRef.current = new window.google.maps.places.Autocomplete(
          inputRef.current,
          {
            types: ["geocode"],
            fields: ["formatted_address", "geometry", "address_components"],
            componentRestrictions: restrictCountries?.length
              ? { country: restrictCountries }
              : undefined
          }
        );

        acRef.current.addListener("place_changed", () => {
          const place = acRef.current.getPlace();
          if (!place?.geometry?.location) return;

          const formatted = place.formatted_address || "";
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const pincode = parsePincode(place.address_components || []);

          // Set input text
          if (inputRef.current) inputRef.current.value = formatted;

          onChangeValue?.(formatted);

          onSelectPlace?.({
            googleAddress: formatted,
            latitude: lat,
            longitude: lng,
            pincode
          });
        });
      } catch (err) {
        console.error("Google Maps load error:", err);
      }
    };

    init();
  }, []);

  return (
    <div>
      <Label>{label}</Label>
      <Input
        ref={inputRef}
        defaultValue={value || ""}
        placeholder="Search address..."
        autoComplete="off"
        onChange={(e) => onChangeValue?.(e.target.value)}
      />
    </div>
  );
}

export default GoogleAddressSearch;
